import { useEffect, useState } from "react";
import { PublicFeedEvent } from "@vb/rgs-contract";
import type { FeedRow } from "../components/LivePlayerFeed.js";

const MAX_ROWS = 50;

interface RecentRow {
  roundIdShort: string;
  playerTag: string;
  operatorId: string;
  outcome: "win" | "bust";
  multiplier: number | null;
  betAmount: number;
  payout: number;
  ts: number;
  /** Optional for back-compat — pre-multi-game rows are tagged as crash. */
  gameSlug?: "vault-breaker" | "pressure-plate" | "cipher-crack";
}

function eventToRow(ev: PublicFeedEvent): FeedRow {
  return {
    name: ev.playerTag,
    bet: ev.betAmount,
    multi: ev.outcome === "win" ? ev.multiplier : null,
    gameSlug: ev.gameSlug,
  };
}

function recentToRow(r: RecentRow): FeedRow {
  return {
    name: r.playerTag,
    bet: r.betAmount,
    multi: r.outcome === "win" ? r.multiplier : null,
    gameSlug: r.gameSlug,
  };
}

/**
 * Subscribes to the server's `/feed/public` WebSocket and accumulates
 * anonymized round resolutions across the platform. Auto-reconnects on
 * close. Seeds with a REST call to `/public/feed/recent` so the table isn't
 * empty on first paint. Returns the rolling row set in newest-first order.
 *
 * Same VITE_WS_BASE rule as `useWsStream` — Vercel can't proxy WS upgrades,
 * so we hit the API host directly when configured. The REST seed goes
 * through Vercel's standard `/public/*` rewrite.
 */
export function usePublicFeed(): FeedRow[] {
  const [rows, setRows] = useState<FeedRow[]>([]);

  // Seed from recent-rounds REST endpoint on mount.
  useEffect(() => {
    let cancelled = false;
    fetch("/public/feed/recent")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { rows?: RecentRow[] } | null) => {
        if (cancelled || !data?.rows) return;
        setRows(data.rows.map(recentToRow));
      })
      .catch(() => {
        // best-effort seed; ignore failures
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Subscribe to live WS events.
  useEffect(() => {
    let stopped = false;
    let ws: WebSocket | null = null;
    let reconnectDelay = 500;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = (): void => {
      if (stopped) return;
      const wsBase = (import.meta as unknown as { env: { VITE_WS_BASE?: string } })
        .env.VITE_WS_BASE;
      const url = wsBase
        ? `${wsBase.replace(/\/$/, "")}/feed/public`
        : `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/feed/public`;

      ws = new WebSocket(url);

      ws.addEventListener("open", () => {
        reconnectDelay = 500;
      });

      ws.addEventListener("message", (ev) => {
        try {
          const raw = JSON.parse(String(ev.data));
          const parsed = PublicFeedEvent.safeParse(raw);
          if (!parsed.success) return;
          setRows((prev) => [eventToRow(parsed.data), ...prev].slice(0, MAX_ROWS));
        } catch {
          // ignore malformed
        }
      });

      ws.addEventListener("close", () => {
        if (stopped) return;
        reconnectTimer = setTimeout(connect, reconnectDelay);
        reconnectDelay = Math.min(5000, reconnectDelay * 2);
      });

      ws.addEventListener("error", () => {
        // close will follow
      });
    };

    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, []);

  return rows;
}
